import React, { useState } from "react";
import { Table, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../utils";
import Paging from "./Paging";
import { FaSort } from "react-icons/fa";
import NoResults from "./NoResults";
import SkeletonTable from "./SkeletonTable";
import ActionDropdown from "./ActionDropdown";
import MobileCardGrid from "./MobileCardGrid";

const Grid = ({
  data,
  displayColumns,
  idColumn,
  idColumn2,
  actions,
  pageNumber,
  totalPages,
  loading,
  placeHolderLinesCount,
  onPageChange,
  onSort,
  selectedRunbook,
  onRowClick,
  useMobileCards = false,
  // Opt-in: instead of a fixed maxHeight scroll region, grow to fill whatever flex
  // space the parent gives (e.g. a resizable DesktopWindow) via flex:1 + min-height:0.
  // Standalone-page callers don't pass this, so their layout is unaffected.
  fillHeight = false,
  // Optional display-only header overrides, keyed by column name. The sort key sent to the
  // server is still derived from the column name (see handleSortClick's caller below), so
  // relabeling a header never changes what the API is asked to sort by.
  columnLabels,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const columns = displayColumns ?? Object.keys(data[0] || {});
  const [orderBy, setOrderBy] = useState(-1);
  const [orderDirection, setOrderDirection] = useState(1);

  const handleSortClick = (orderByColumn) => {
    let orderDirectionResult;

    if (orderByColumn === orderBy) {
      orderDirectionResult = orderDirection * -1;
    } else {
      orderDirectionResult = 1;
    }

    setOrderBy(orderByColumn);
    setOrderDirection(orderDirectionResult);

    onSort(
      orderByColumn,
      orderDirectionResult === 1 ? "Asc" : "Desc",
      location,
      navigate
    );
  };

  const headerLabel = (column) => columnLabels?.[column] ?? Utils.fixCamelCase(column);

  const fillHeightStyle = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

  return (
    <div style={fillHeight ? fillHeightStyle : undefined}>
      {loading && <SkeletonTable columns={displayColumns?.length || 5} rows={placeHolderLinesCount ?? 8} />}
      {!loading && (
        <div style={fillHeight ? fillHeightStyle : undefined}>
          {totalPages > 0 && (
            <>
              {/* Mobile Card View - Only show if enabled */}
              {useMobileCards && (
                <MobileCardGrid
                  data={data}
                  displayColumns={displayColumns}
                  idColumn={idColumn}
                  idColumn2={idColumn2}
                  selectedRunbook={selectedRunbook}
                  onRowClick={onRowClick}
                />
              )}

              {/* Desktop Table View (or mobile table if cards disabled) */}
              <div
                className={useMobileCards ? "d-none d-md-block" : ""}
                style={
                  fillHeight
                    ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto" }
                    : { maxHeight: "600px", overflowY: "auto" }
                }
              >
                <Table hover striped>
                <thead>
                  <tr>
                    {columns
                      .filter((c) => c !== "support")
                      .map((column) => (
                        <th key={column} className="text-nowrap">
                          {onSort && (
                            <Button
                              variant="link"
                              className="text-decoration-none text-dark ps-0 fw-bold"
                              onClick={() =>
                                handleSortClick(
                                  Utils.fixCamelCase(column).replaceAll(" ", "")
                                )
                              }
                            >
                              {headerLabel(column)}
                              <FaSort className="ms-2 mb-1 text-primary" />
                            </Button>
                          )}
                          {!onSort && <>{headerLabel(column)}</>}
                        </th>
                      ))}
                    {actions && <th></th>}
                    {columns
                      .filter((c) => c === "support")
                      .map((column) => (
                        <th key={column} className="text-nowrap"></th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const isSelected = selectedRunbook &&
                      selectedRunbook[idColumn] === item[idColumn] &&
                      (!idColumn2 || selectedRunbook[idColumn2] === item[idColumn2]);

                    return (
                      <tr
                        key={index}
                        className={`${isSelected ? 'table-primary' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                        onClick={() => onRowClick && onRowClick(item)}
                        style={onRowClick ? { cursor: 'pointer', userSelect: 'none' } : {}}
                      >
                        {columns
                          .filter((c) => c !== "support")
                          .map((column) => (
                            <td key={column}>{item[column]}</td>
                          ))}
                        {actions && (
                          <td className="text-nowrap">
                            <ActionDropdown
                              actions={actions}
                              item={item}
                              idColumn={idColumn}
                              idColumn2={idColumn2}
                            />
                          </td>
                        )}
                        {columns
                          .filter((c) => c === "support")
                          .map((column) => (
                            <td key={column}>{item[column]}</td>
                          ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              </div>

              {totalPages > 1 && (
                <Paging
                  currentPage={pageNumber}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              )}
            </>
          )}
        </div>
      )}
      {!loading && totalPages === 0 && (
        <div className="d-flex justify-content-center">
          <NoResults />
        </div>
      )}
    </div>
  );
};

export default Grid;
